import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import gsap from "gsap"
import GSDevTools from "gsap/GSDevTools"
import type { AnimationI, AnySceneObjectT, TextI, TickerI, TimelineLabelI } from "tweenly"
import * as tweenly from "tweenly"
import { Asset } from "tweenly/dist/fetchAsset"
import { imageSrc } from "../../data/styles/simple/display.styles"
import useDynamicRefs from "../../hooks/useDynamicRefs"
import { AppThunkT } from "../store"

import {
    buildSequenceDescriptors,
    configureSequencePlayer,
    EMPTY_IMAGE_SRC,
    paintSequences,
    parseTextFields,
    roundNumber,
    serializeTextFields,
    waitForFontsAndReflow,
} from "tweenly"
import { renderGroupStyleValue } from "../../data/styles/groups/groups.styles"
import { renderSimpleStyleValue } from "../../data/styles/simple/simple.styles"
import { sumArray } from "../../helpers/array.helpers"
import {
    CRAWL_CONTINUES_DURATION,
    CRAWL_STAGGERED_DURATION,
} from "../../helpers/crawlTicker.helpers"
import { resetGsapTimelines } from "../../helpers/masterTimeline.helpers"
import { selectUndoable } from "../../helpers/selector.helpers"
import { CrawlContinuousTicker } from "../../helpers/tickers/CrawlContinuousTicker"
import { CrawlStaggeredTicker } from "../../helpers/tickers/CrawlStaggeredTicker"
import { ScrollDownTicker } from "../../helpers/tickers/ScrollDownTicker"
import { ScrollUpDownTicker } from "../../helpers/tickers/ScrollUpDownTicker"
import { ScrollUpTicker } from "../../helpers/tickers/ScrollUpTicker"
import {
    getTweenOwner,
    initTweenForPreview,
    serializeAnimationStateForPreview,
} from "../../helpers/tween.helpers"
import { canUpdateTweensInPlace, collectSubtreeIds } from "../../helpers/timelineRebuild.helpers"
import { selectAllAnimations } from "./objects.slice/animation/selectAllAnimations"
import { initSubTimelineAction } from "./subTimelines.slice"
gsap.registerPlugin(GSDevTools)

type StateT = {
    value: any | null
    time: number
    isPlaying: boolean
}

const initialState: StateT = {
    value: null,
    time: 0,
    isPlaying: false,
}

type PayloadT = {
    gsapTimeline: GSAPTimeline | null
}

export const masterTimelineSlice = createSlice({
    name: "masterTimeline",
    initialState,
    reducers: {
        updMasterTimelineAction: (state: StateT, action: PayloadAction<PayloadT>) => {
            state.value = action.payload.gsapTimeline
        },
        setTimeToSeek: (state: StateT, action: PayloadAction<number>) => {
            state.time = action.payload
        },
        setIsPlaying: (state: StateT, action: PayloadAction<boolean>) => {
            state.isPlaying = action.payload
        },
    },
})

/**
 * Manually synchronizes the DOM styles of all scene objects
 * with their current values from the Redux state.
 * Uses GSAP clearProps to properly reset inline styles set by animations.
 */
/**
 * @param onlyObjectIds when given, only those objects are touched. A targeted tween update
 * has no reason to rewrite the inline styles of the whole scene.
 */
export const refreshAllStyles =
    (onlyObjectIds?: string[]): AppThunkT =>
    (dispatch, getState) => {
        const objects = selectUndoable(getState()).objects
        const [getRef] = useDynamicRefs<HTMLElement>()

        const refreshStyles = (object: AnySceneObjectT, element: HTMLElement) => {
            Object.values(object.styles).forEach((style) => {
                const styleProperty = style.property as any
                // Compare the RENDERED value, not the raw one. `style.value` is a number for
                // opacity / zIndex / ... and carries no unit for width / top / ..., while
                // `element.style[prop]` is always a string ("1", "400px"), so
                // `style.value !== element.style[prop]` was true for every numeric style of every
                // object. The guard filtered nothing and this loop rewrote the whole scene —
                // roughly 7 200 inline styles — on every single rebuild, which is what makes
                // `Recalculate Style` the dominant cost of a drag.
                //
                // Only redundant writes disappear: anything GSAP actually changed (opacity mid
                // fade, transforms) still differs from the Redux value and is still written.
                const value = String(renderSimpleStyleValue(style, "css"))
                if (value !== element.style[styleProperty]) {
                    element.style[styleProperty] = value
                }
            })
            if (object.transforms && Object.values(object.transforms).length > 0) {
                const transform = Object.values(object.transforms)
                    .map((t) => renderGroupStyleValue(t, "css"))
                    .join(" ")

                if (element.style.transform !== transform) {
                    element.style.setProperty("transform", transform)
                }
            } else if (element.style.transform !== "none") {
                element.style.setProperty("transform", "none")
            }
        }

        const candidates: AnySceneObjectT[] = onlyObjectIds
            ? (onlyObjectIds.map((id) => objects.value[id]).filter(Boolean) as AnySceneObjectT[])
            : Object.values(objects.value)

        const elements: HTMLElement[] = []
        const scopedObjects: AnySceneObjectT[] = []
        candidates.forEach((object: AnySceneObjectT) => {
            const element = getRef(object.id)?.current
            if (!element) return
            elements.push(element)
            scopedObjects.push(object)
        })

        if (elements.length === 0) return

        // ONE gsap.set for every element instead of one per object. Clear GSAP-set transform
        // properties before applying the Redux state: when switching animations (e.g.
        // slideToRight -> rotateY) GSAP retains old inline transform values (translateX) that
        // conflict with the new ones. Each `gsap.set` builds a Tween and puts it on the global
        // timeline, so doing it per object meant a few hundred throwaway tweens per rebuild —
        // which `resetGsapTimelines` then has to walk and kill again on the next one.
        gsap.set(elements, { clearProps: "transform" })

        scopedObjects.forEach((object, index) => refreshStyles(object, elements[index]))
    }

/**
 * Replace the tweens of one object subtree on the EXISTING master timeline.
 *
 * The full `updateMasterTimeline` kills every tween in the scene, builds ~300 new ones and
 * rewrites the inline styles of every object — for a change as small as one colour. This does
 * the same job for the objects that actually changed, and leaves everything else alone.
 *
 * Deliberately does NOT create a new GSAP timeline: `state.masterTimeline.value` keeps its
 * identity, so `useMasterTimeline` does not change and nothing in the timeline UI re-renders.
 * The instance is only republished when the duration really moved.
 *
 * @returns true when the update was handled here; false means the caller must fall back to
 * the full rebuild.
 */
export const updateObjectTweens =
    (changedIds: string[] | null): AppThunkT<boolean> =>
    (dispatch, getState) => {
        if (!changedIds || changedIds.length === 0) return false

        const state = getState()
        const masterTimeline: GSAPTimeline | null = state.masterTimeline.value
        if (!masterTimeline) return false

        const { objects } = selectUndoable(state)
        const subtreeIds = collectSubtreeIds(objects.value, changedIds)
        if (!canUpdateTweensInPlace(objects, subtreeIds)) return false

        const [getRef] = useDynamicRefs()
        const scope = new Set(subtreeIds)
        const durationBefore = masterTimeline.duration()

        // `kill()` with no arguments runs GSAP's `_interrupt`, which also unlinks the tween
        // from its parent (gsap-core.js:3461) — no separate `remove()` needed.
        masterTimeline.getChildren(false, true, true).forEach((child) => {
            const owner = getTweenOwner(child)
            if (owner && scope.has(owner.objectId)) child.kill()
        })

        // Reset the subtree to its Redux styles BEFORE the new tweens exist. Killing a tween
        // does not revert what it rendered, so the DOM still holds the old animated values: a
        // `to` tween would record them as its start values, and refreshing AFTER the render
        // below would overwrite the animated state at the playhead (an object half way through
        // a fadeIn / rotate / slideFrom jumped to its final style after every move or resize).
        dispatch(refreshAllStyles(subtreeIds))

        const newTweens: GSAPTween[] = []
        subtreeIds.forEach((id) => {
            const object = objects.value[id]
            Object.values(object?.animations ?? {}).forEach((animation) => {
                if (!animation) return
                const tween = initTweenForPreview(animation, getRef)
                masterTimeline.add(tween, animation.delay)
                newTweens.push(tween)
            })
        })

        // Render only the tweens that were just added, at the playhead. `seek()` at the current
        // time would do nothing: Animation.totalTime only renders when the time actually moved
        // (gsap-core.js:1679), so `force` is needed — but force-rendering the whole master
        // timeline rewrote the styles of every animated object in the scene (with identical
        // values) and fired its onUpdate (sequence painting, label checks) without the time
        // having moved. Like the master's own render, a tween that has not started yet is
        // skipped, and tweens on the same object are rendered in timeline order.
        // read the playhead off GSAP itself: `state.masterTimeline.time` is only updated by
        // explicit seeks, not by playback stopping on a pause label or at the end
        const time = masterTimeline.time()
        newTweens
            .filter((tween) => tween.startTime() <= time)
            .sort((a, b) => a.startTime() - b.startTime())
            .forEach((tween) => tween.render(time - tween.startTime(), false, true))

        // adding or removing tweens can move the end of the timeline; the timeline UI reads
        // the duration off this instance, so it has to be republished when that happens
        if (masterTimeline.duration() !== durationBefore) {
            dispatch(updMasterTimelineAction({ gsapTimeline: masterTimeline }))
        }

        return true
    }

//to do objects, animations and timeline drill as props
export const updateMasterTimeline =
    (timeToSeek: number): AppThunkT =>
    async (dispatch, getState) => {
        const { objects } = selectUndoable(getState())
        const animations = selectAllAnimations(Object.values(objects.value))
        const { timeline, assets } = getState()
        const subTimelines = getState().subTimelines.value

        const [getRef] = useDynamicRefs()

        if (objects.value === null || Object.keys(objects.value).length === 0) {
            dispatch(updMasterTimelineAction({ gsapTimeline: null }))
            return null
        }

        resetGsapTimelines()

        // Ensure all DOM element styles match Redux state BEFORE the new tweens are created and
        // rendered at `timeToSeek` — refreshing afterwards overwrote the animated state at the
        // playhead (opacity of a running fadeIn, transforms of rotate / slideFrom, ...).
        dispatch(refreshAllStyles())

        // Decode-gated image-sequence player (mirrors the export runtime): frames are
        // driven by declarative `data-tw-seq` markers + a rolling, decode-gated painter
        // instead of per-frame `attr.src` writes.
        const { descriptors: sequenceDescriptors, frameIndexById } =
            buildSequenceDescriptors(animations)
        configureSequencePlayer(sequenceDescriptors)

        const newMasterTimeline: GSAPTimeline = gsap.timeline({
            paused: true,
            id: "master",
        })

        const zeroAnimation = animations.find(
            (a) => a && a.delay === 0 && a.tween.name === "sequence"
        )

        //set empty image src for active object on position 0 on timeline when zeroAnimation not exist
        const selectedIds = getState().activeObject.selected
        if (!zeroAnimation) {
            selectedIds.forEach((id) => {
                const currentObject = objects.value[id]
                if (currentObject?.type === "sequence") {
                    const emptyTween = gsap.set(
                        getRef(id)?.current as GSAPTweenTarget,
                        serializeAnimationStateForPreview([imageSrc(EMPTY_IMAGE_SRC)])
                    )
                    newMasterTimeline.add(emptyTween, 0)
                }
            })
        }

        // Group by objectId animations with subTimeline === true
        const groupedAnimations: { [key: string]: AnimationI[] } = animations
            .filter((animation) => animation.subTimeline === true)
            .reduce((acc, animation) => {
                if (!animation.parentId) {
                    const { objectId } = animation
                    if (!acc[objectId]) {
                        acc[objectId] = []
                    }
                    acc[objectId].push(animation)
                } else {
                    const { parentId } = animation
                    if (!acc[parentId]) {
                        acc[parentId] = []
                    }
                    acc[parentId].push(animation)
                }
                return acc
            }, {} as { [key: string]: AnimationI[] })

        // Iterate over each group and create subTimeline
        for (const objectId of Object.keys(groupedAnimations)) {
            const animationGroup = groupedAnimations[objectId]

            subTimelines[objectId]?.clear()
            subTimelines[objectId]?.kill()

            //[NOTE]: pri prvotnom nacitani vytvori dva krat gsap subtimelien s dvomi rozlicnymi referenciami aj napriek tomu ze ukladam iba jednu do subtimeline.slice
            // problem bude pravdepdobne nastavat preto lebo gsap si drzi v DOM-e vlastnu referenciu aj napriek tomu ze obsluha danej subtimeline by mala byt v subtimeline.slice
            // vidiet to ked sa prida parameter paused: true,
            let newTickerTimeline: GSAPTimeline = gsap.timeline({
                repeat: 0,
                id: objectId,
            })

            // Create gsapTween from each animations and add to tickerTimeline

            for (const animation of animationGroup) {
                if (
                    !animation.subTimelineType?.startsWith("crawl") &&
                    animation.subTimelineType !== "scrollDown" &&
                    animation.subTimelineType !== "scrollUp" &&
                    animation.subTimelineType !== "scrollUpDown"
                ) {
                    const gsapTween: GSAPTween = initTweenForPreview(animation, getRef)
                    newTickerTimeline.add(gsapTween, animation.delay)
                }
            }
            if (animationGroup[0].subTimelineType === "scrollDown") {
                const maskObject = objects.value[objectId] as TickerI

                let childTexts: string[] = []
                for (const childId of maskObject.childIds) {
                    const textObject = objects.value[childId] as TextI
                    childTexts.push(textObject.text)
                }
                const textList1Node = document.getElementById(objectId)
                const joinedTexts = await tweenly.processField(
                    serializeTextFields(childTexts),
                    assets.data as Asset[]
                )
                let splittedTexts: string[] = []
                if (joinedTexts) {
                    splittedTexts = parseTextFields(joinedTexts)
                }
                let splittedTextsIds: Record<string, string> = {}
                splittedTexts.forEach(
                    (splText, index) => (splittedTextsIds[maskObject.childIds[index]] = splText)
                )
                for (const childId of maskObject.childIds) {
                    const element = document.getElementById(childId) as HTMLElement
                    const newText = splittedTextsIds[childId]
                    if (newText) {
                        element.innerHTML = newText
                    }
                }

                const ticker = new ScrollDownTicker({
                    timeline: newTickerTimeline,
                    objectId,
                    maskObject,
                    animationGroup,
                    textListNode: textList1Node,
                    joinedTexts: joinedTexts ?? null,
                    scrollTime: maskObject.duration ?? 5,
                    scrollPause: maskObject.delay ?? 2,
                    childTexts,
                    assets: assets.data as Asset[],
                })

                ticker.start()
            } else if (animationGroup[0].subTimelineType === "scrollUp") {
                const maskObject = objects.value[objectId] as TickerI

                let childTexts: string[] = []
                for (const childId of maskObject.childIds) {
                    const textObject = objects.value[childId] as TextI
                    childTexts.push(textObject.text)
                }
                const textList1Node = document.getElementById(objectId)
                const joinedTexts = await tweenly.processField(
                    serializeTextFields(childTexts),
                    assets.data as Asset[]
                )
                let splittedTexts: string[] = []
                if (joinedTexts) {
                    splittedTexts = parseTextFields(joinedTexts)
                }
                let splittedTextsIds: Record<string, string> = {}
                splittedTexts.forEach(
                    (splText, index) => (splittedTextsIds[maskObject.childIds[index]] = splText)
                )
                for (const childId of maskObject.childIds) {
                    const element = document.getElementById(childId) as HTMLElement
                    const newText = splittedTextsIds[childId]
                    if (newText) {
                        element.innerHTML = newText
                    }
                }

                const ticker = new ScrollUpTicker({
                    timeline: newTickerTimeline,
                    objectId,
                    maskObject,
                    animationGroup,
                    textListNode: textList1Node,
                    joinedTexts: joinedTexts ?? null,
                    scrollTime: maskObject.duration ?? 5,
                    scrollPause: maskObject.delay ?? 2,
                    childTexts,
                    assets: assets.data as Asset[],
                })

                ticker.start()
            } else if (animationGroup[0].subTimelineType === "scrollUpDown") {
                const maskObject = objects.value[objectId] as TickerI
                let childTexts: string[] = []
                for (const childId of maskObject.childIds) {
                    const textObject = objects.value[childId] as TextI
                    childTexts.push(textObject.text)
                }
                const textList1Node = document.getElementById(objectId)
                const joinedTexts = await tweenly.processField(
                    serializeTextFields(childTexts),
                    assets.data as Asset[]
                )
                let splittedTexts: string[] = []
                if (joinedTexts) {
                    splittedTexts = parseTextFields(joinedTexts)
                }
                let splittedTextsIds: Record<string, string> = {}
                splittedTexts.forEach(
                    (splText, index) => (splittedTextsIds[maskObject.childIds[index]] = splText)
                )

                for (const childId of maskObject.childIds) {
                    const element = document.getElementById(childId) as HTMLElement
                    const newText = splittedTextsIds[childId]
                    if (newText) {
                        element.innerHTML = newText
                    }
                }

                const ticker = new ScrollUpDownTicker({
                    timeline: newTickerTimeline,
                    objectId,
                    maskObject,
                    animationGroup,
                    textListNode: textList1Node,
                    joinedTexts: joinedTexts ?? null,
                    scrollTime: maskObject.duration ?? 5,
                    scrollPause: maskObject.delay ?? 2,
                    childTexts,
                    assets: assets.data as Asset[],
                })

                ticker.start()
            } else if (animationGroup[0].subTimelineType === "crawlStaggered") {
                const maskObject = objects.value[objectId] as TickerI
                let childTexts: string[] = []
                for (const childId of maskObject.childIds) {
                    const textObject = objects.value[childId] as TextI
                    childTexts.push(textObject.text)
                }
                const textList1Node = document.getElementById(objectId)
                const joinedTexts = await tweenly.processField(
                    serializeTextFields(childTexts),
                    assets.data as Asset[]
                )
                let splittedTexts: string[] = []
                if (joinedTexts) {
                    splittedTexts = parseTextFields(joinedTexts)
                }
                let splittedTextsIds: Record<string, string> = {}
                splittedTexts.forEach(
                    (splText, index) => (splittedTextsIds[maskObject.childIds[index]] = splText)
                )
                let sumWidth: number[] = []
                let lastWidth: number = 0
                let newWidths: Record<string, number> = {}

                // First pass: set all text content
                for (const childId of maskObject.childIds) {
                    const element = document.getElementById(childId) as HTMLElement
                    const newText = splittedTextsIds[childId]
                    if (newText && element) {
                        element.innerHTML = newText
                    }
                }

                // Wait for fonts to load and force reflow before measuring widths
                await waitForFontsAndReflow()

                // Second pass: measure widths after reflow
                for (const childId of maskObject.childIds) {
                    const element = document.getElementById(childId) as HTMLElement
                    if (element && splittedTextsIds[childId]) {
                        newWidths[childId] = element.clientWidth || 0
                        sumWidth.push(element.clientWidth)
                    }
                }
                const maskWidth = textList1Node?.clientWidth ?? 0
                let textsWidth = sumArray(sumWidth)
                const duration: number =
                    maskObject && maskObject?.duration
                        ? maskObject?.duration
                        : CRAWL_STAGGERED_DURATION

                const ticker = new CrawlStaggeredTicker({
                    timeline: newTickerTimeline,
                    objectId,
                    maskObject,
                    animationGroup,
                    textListNode: textList1Node,
                    joinedTexts: joinedTexts ?? null,
                    maskWidth,
                    duration,
                    lastWidth,
                    textsWidth,
                    newWidths,
                    childTexts,
                    assets: assets.data as Asset[],
                })

                ticker.start()
            } else if (animationGroup[0].subTimelineType === "crawlContinuous") {
                const maskObject = objects.value[objectId] as TickerI

                let childTexts: string[] = []
                for (const childId of maskObject.childIds) {
                    const textObject = objects.value[childId] as TextI
                    childTexts.push(textObject.text)
                }
                const textList1Node = document.getElementById(objectId)
                const joinedTexts = await tweenly.processField(
                    serializeTextFields(childTexts),
                    assets.data as Asset[]
                )
                let splittedTexts: string[] = []
                if (joinedTexts) {
                    splittedTexts = parseTextFields(joinedTexts)
                }
                let splittedTextsIds: Record<string, string> = {}
                splittedTexts.forEach(
                    (splText, index) => (splittedTextsIds[maskObject.childIds[index]] = splText)
                )
                let sumWidth: number[] = []
                let lastWidth: number = 0
                let newWidths: Record<string, number> = {}

                // First pass: set all text content
                for (const childId of maskObject.childIds) {
                    const element = document.getElementById(childId) as HTMLElement
                    const newText = splittedTextsIds[childId]
                    if (newText && element) {
                        element.innerHTML = newText
                    }
                }

                // Wait for fonts to load and force reflow before measuring widths
                await waitForFontsAndReflow()

                // Second pass: measure widths after reflow
                for (const childId of maskObject.childIds) {
                    const element = document.getElementById(childId) as HTMLElement
                    if (element && splittedTextsIds[childId]) {
                        newWidths[childId] = element.clientWidth || 0
                        sumWidth.push(element.clientWidth)
                    }
                }
                const maskWidth = textList1Node?.clientWidth ?? 0
                let textsWidth = sumArray(sumWidth)
                const duration: number =
                    maskObject && maskObject?.duration
                        ? maskObject?.duration
                        : CRAWL_CONTINUES_DURATION

                const ticker = new CrawlContinuousTicker({
                    timeline: newTickerTimeline,
                    objectId,
                    maskObject,
                    animationGroup,
                    textListNode: textList1Node,
                    joinedTexts: joinedTexts ?? null,
                    maskWidth,
                    duration,
                    lastWidth,
                    textsWidth,
                    newWidths,
                    childTexts,
                    assets: assets.data as Asset[],
                })

                ticker.start()
            }
            // Initialized subTimeline
            dispatch(
                initSubTimelineAction({
                    id: objectId,
                    timeline: newTickerTimeline,
                })
            )
        }

        // Add a zero-duration `data-tw-seq` marker at `delay` for a sequence frame.
        // The decode-gated painter reads this attribute and paints the matching frame.
        const addSequenceMarker = (objectId: string, index: number, delay: number) => {
            const el = getRef(objectId)?.current as GSAPTweenTarget | undefined
            const markerTween = el
                ? gsap.set(el, { attr: { "data-tw-seq": index } })
                : // preserve timeline duration/position even if the element is absent
                  gsap.to({}, { duration: 0 })
            newMasterTimeline.add(markerTween, delay)
        }

        animations
            .filter((animation) => !animation.subTimeline)
            .forEach((animation: AnimationI) => {
                if (animation.tween.name === "sequence") {
                    const index = frameIndexById[animation.id] ?? 0
                    addSequenceMarker(animation.objectId, index, animation.delay)

                    const nextDelay = roundNumber(animation.delay + animation.duration, 2)
                    const nextAnimation = animations.find(
                        (a) =>
                            a &&
                            a.objectId === animation.objectId &&
                            roundNumber(a.delay, 2) === nextDelay &&
                            a.tween.name === "sequence"
                    )

                    if (!nextAnimation) {
                        // terminating empty frame -> blank marker (keeps old semantics)
                        addSequenceMarker(animation.objectId, -1, nextDelay)
                    }
                } else {
                    const gsapTween: GSAPTween = initTweenForPreview(animation, getRef)
                    newMasterTimeline.add(gsapTween, animation.delay)
                }
            })

        if (timeline.value.labels) {
            Object.values(timeline.value.labels).forEach((label: TimelineLabelI) => {
                newMasterTimeline.addLabel(label.title, label.time)
            })
        }

        if (timeline.value.freezeTime !== null) {
            newMasterTimeline.pause(timeline.value.freezeTime, false)
        } else {
            newMasterTimeline.pause(timeToSeek, false)
        }

        dispatch(updMasterTimelineAction({ gsapTimeline: newMasterTimeline }))

        // Paint the sequence frame for the current playhead. The timeline was just
        // rendered at `timeToSeek` (pause above), so the `data-tw-seq` markers are in
        // the DOM. Live playback / scrubbing repaint via the onUpdate hook.
        paintSequences()
    }

export const { updMasterTimelineAction, setTimeToSeek, setIsPlaying } = masterTimelineSlice.actions
export default masterTimelineSlice.reducer
